/**
 * Device-token client for native-mobile Face ID / Touch ID sign-in.
 *
 * Companion to `nativeBridge.js`. `NativeApp.biometricAuth()` only proves
 * the user is present at the device - it never returns a session. So on
 * the FIRST successful password login inside the native app we call
 * `registerDeviceToken()` to mint a long-lived, revocable, device-scoped
 * credential, save it locally, and then on EVERY subsequent login the
 * button flow becomes:
 *
 *    biometricAuth() ─success─▸ redeemDeviceToken() ─▸ applyBiometricLoginResult()
 *
 * The token is opaque to the client; we never inspect it. Local
 * persistence lives in localStorage (namespaced per email so multiple
 * users can share a device); the server holds only a SHA-256 hash.
 *
 * On sign-out or 401/token revoke we clear the local copy AND fire a
 * best-effort `revokeDeviceToken()` so the server row is closed out.
 */
import axios from 'axios';
import { isNativeApp, nativePlatform } from './nativeBridge';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/auth/device-token`;

// ─── Local storage keys ──────────────────────────────────────────
const DEVICE_ID_KEY = 'akf_native_device_id';
const TOKEN_KEY_PREFIX = 'akf_device_token::';       // + email
const HAS_TOKEN_FLAG_PREFIX = 'akf_has_device_token::'; // + email (fast sync check)

const _emailKey = (email) => (email || '').trim().toLowerCase();

/**
 * Return a stable per-installation device id. We first try the native
 * bridge (if it exposes one), then fall back to a UUID we cache in
 * localStorage. The id is a client-side identifier only - the server
 * never trusts it beyond scoping the token, so guessing another
 * user's device_id yields nothing.
 */
export function getOrCreateDeviceId() {
  try {
    const native = typeof window !== 'undefined' && window.NativeApp?.deviceId;
    if (native && typeof native === 'string' && native.length >= 6) return native;
  } catch (_) { /* noop */ }
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `dev-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (_) {
    return 'dev-ephemeral';
  }
}

// ─── Local persistence ───────────────────────────────────────────

export function saveDeviceToken(email, token, meta = {}) {
  const ek = _emailKey(email);
  if (!ek || !token) return;
  try {
    localStorage.setItem(TOKEN_KEY_PREFIX + ek, JSON.stringify({
      token,
      device_label: meta.device_label || null,
      created_at: meta.created_at || new Date().toISOString(),
      token_id: meta.token_id || null,
    }));
    localStorage.setItem(HAS_TOKEN_FLAG_PREFIX + ek, '1');
  } catch (_) { /* quota / private mode */ }
}

export function getDeviceToken(email) {
  const ek = _emailKey(email);
  if (!ek) return null;
  try {
    const raw = localStorage.getItem(TOKEN_KEY_PREFIX + ek);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) { return null; }
}

/** Sync (no network) check for button-visibility decisions. */
export function hasDeviceTokenLocally(email) {
  const ek = _emailKey(email);
  if (!ek) return false;
  try { return localStorage.getItem(HAS_TOKEN_FLAG_PREFIX + ek) === '1'; }
  catch { return false; }
}

export function clearDeviceToken(email) {
  const ek = _emailKey(email);
  if (!ek) return;
  try {
    localStorage.removeItem(TOKEN_KEY_PREFIX + ek);
    localStorage.removeItem(HAS_TOKEN_FLAG_PREFIX + ek);
  } catch (_) { /* noop */ }
}

// ─── API calls ───────────────────────────────────────────────────

/**
 * Register a device_token for the current logged-in user. Call this
 * right after a successful biometric enrollment inside the native app.
 * The user must be authenticated (cookies) for this to succeed.
 */
export async function registerDeviceToken({ email, biometryType } = {}) {
  const device_id = getOrCreateDeviceId();
  const platform = nativePlatform() || (isNativeApp() ? 'unknown-native' : 'web-pwa');
  const device_label = (() => {
    try {
      const ua = navigator.userAgent || '';
      if (/iPhone/.test(ua)) return 'iPhone';
      if (/iPad/.test(ua)) return 'iPad';
      if (/Android/.test(ua)) return 'Android device';
      return 'This device';
    } catch { return 'This device'; }
  })();

  const res = await axios.post(`${BASE}/register`, {
    device_id,
    device_label,
    platform,
    biometry_type: biometryType || null,
  }, { withCredentials: true });

  if (res?.data?.device_token && email) {
    saveDeviceToken(email, res.data.device_token, {
      device_label: res.data.device_label,
      created_at: res.data.created_at,
      token_id: res.data.token_id,
    });
  }
  return res.data;
}

/**
 * Trade the stored device_token for a fresh session. Call this AFTER a
 * successful `biometricAuth()`. Returns the same shape as the passkey
 * verify endpoint - `applyBiometricLoginResult(res)` handles it as-is.
 * Throws on 401 (invalid/revoked/expired token).
 */
export async function redeemDeviceToken(email) {
  const stored = getDeviceToken(email);
  if (!stored?.token) {
    const err = new Error('no_device_token_for_email');
    err.code = 'no_device_token';
    throw err;
  }
  try {
    const res = await axios.post(`${BASE}/redeem`, {
      device_token: stored.token,
      email: (email || '').trim().toLowerCase(),
    }, { withCredentials: true });
    return res.data;
  } catch (e) {
    // Server said the token is bad - drop it locally so we stop showing
    // the button until re-enrolled.
    if (e?.response?.status === 401) clearDeviceToken(email);
    throw e;
  }
}

/**
 * Fire-and-forget revoke - called on sign-out / disable-biometric.
 * Never rejects; failures are logged only.
 */
export async function revokeDeviceToken(email) {
  const stored = getDeviceToken(email);
  clearDeviceToken(email);   // always clear locally, even if network call fails
  if (!stored?.token) return { revoked: 0 };
  try {
    const res = await axios.post(`${BASE}/revoke`, {
      device_token: stored.token,
    }, { withCredentials: true });
    return res.data;
  } catch (e) {
    try { console.warn('[deviceToken] revoke best-effort failed', e?.message); } catch { /* noop */ }
    return { revoked: 0, error: 'network' };
  }
}

/** Active biometric sign-in devices for the signed-in user (Settings). */
export async function listEnrolledDevices() {
  const res = await axios.get(`${API}/api/auth/device-token/devices`, {
    params: { device_id: getOrCreateDeviceId() }, withCredentials: true,
  });
  return res.data?.devices || [];
}

/** Revoke another device's token by id (auth required). */
export async function revokeDeviceTokenById(tokenId) {
  await axios.post(`${API}/api/auth/device-token/revoke`, { token_id: tokenId }, { withCredentials: true });
}
